/**
 * Commit-grouping for canvas cards.
 *
 * One logical commit (a single `doc_apply_text` REST call) groups its
 * paragraphs into one canvas-card. The server stamps `commit_id` on
 * every paragraph of a REST write (Content Issue #16). Paragraphs
 * without a commit_id — WS-typed by humans in the editor, or legacy
 * paragraphs from before the stamp existed — always form their own
 * single-paragraph group and are NEVER merged into a neighbor.
 */

import type { TextBlock } from '@arkturian/canvas-engine'

/**
 * TextBlock plus the server-stamped paragraph attrs that arrived after
 * the canvas-engine interface was published (Issue #10 created_at,
 * Issue #16 commit_id). usePostBlocks forwards both; the engine type
 * will absorb them on its next bump.
 */
export type CanvasBlock = TextBlock & {
  created_at?: string | null
  commit_id?: string | null
}

export interface MergedBlock {
  /** commit_id when present, else the primary's block-id fallback. */
  id: string
  /** First paragraph — drives author, time, paragraph_index, position. */
  primary: CanvasBlock
  /** 1..N paragraphs sharing the same commit, in paragraph order. */
  paragraphs: CanvasBlock[]
  /** Sum of char_count across all paragraphs in this group. */
  combined_char_count: number
}

export function mergeBlocksByCommit(blocks: CanvasBlock[]): MergedBlock[] {
  const result: MergedBlock[] = []
  const sorted = [...blocks].sort(
    (a, b) => a.paragraph_index - b.paragraph_index,
  )
  let current: MergedBlock | null = null
  let currentCid: string | null = null
  for (const b of sorted) {
    const cid = b.commit_id ?? null
    // No commit_id → standalone group.
    if (!cid) {
      if (current) result.push(current)
      current = null
      currentCid = null
      result.push({
        id: b.block_id ?? `_idx_${b.paragraph_index}`,
        primary: b,
        paragraphs: [b],
        combined_char_count: b.char_count,
      })
      continue
    }
    // Same commit as current group → extend.
    if (current && currentCid === cid) {
      current.paragraphs.push(b)
      current.combined_char_count += b.char_count
      continue
    }
    // Different commit or fresh start → flush previous, open new.
    if (current) result.push(current)
    current = {
      id: cid,
      primary: b,
      paragraphs: [b],
      combined_char_count: b.char_count,
    }
    currentCid = cid
  }
  if (current) result.push(current)
  return result
}
